import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { type RcFile } from 'antd/es/upload';

export interface RequestConfig {
  requestUuid: string;
  config: {
    mapping_config: {
      search_key: string;
      mapping: Record<string, unknown>;
      csv_headers: string[];
    };
    import_config: {
      validate_search_key_data: boolean;
    };
  };
}

export const defaultFileMappingConfig = {
  selectedRows: [],
  prevMapping: {},
  searchKey: '',
  fieldErrors: {
    showFieldErrors: false,
    link: '',
    preview: [],
    total_errors: 0,
  },
  lastPayload: {},
  leadSourceConfig: {
    isSelected: false,
    schemaName: '',
    availableSchemas: [],
  },
};

const initialState: {
  currentStep: number;
  uploadedFileConfig: {
    file: RcFile | null | undefined;
    progessPercent: number;
    progressStatus: 'active' | 'exception' | 'success';
  };
  fileConfig: RequestConfig;
  fileMapping: {
    selectedRows: string[];
    prevMapping: Record<string, {
      schemaName: string;
      format: string;
    }>;
    searchKey: string;
    fieldErrors: {
      showFieldErrors: boolean;
      total_errors: number;
      preview: Array<string[]>;
      link: string;
    };
    lastPayload: any;
    leadSourceConfig: {
      isSelected: boolean;
      schemaName: string;
      availableSchemas: string[];
    };
  };
} = {
  currentStep: 0,
  uploadedFileConfig: {
    file: null,
    progessPercent: 0,
    progressStatus: 'active',
  },
  fileConfig: {
    requestUuid: '',
    config: {
      mapping_config: {
        search_key: '',
        mapping: {},
        csv_headers: [],
      },
      import_config: {
        validate_search_key_data: false,
      },
    },
  },
  fileMapping: defaultFileMappingConfig,
};

export const importLeadSlice = createSlice({
  name: 'importLeads',
  initialState,
  reducers: {
    resetImportLead: () => initialState,
    incrementCurrentStep: (state) => {
      state.currentStep += 1;
    },
    decrementCurrentStep: (state) => {
      state.currentStep -= 1;
    },
    updateUploadedFileConfig: (state, action: PayloadAction<{
      file?: RcFile | null | undefined;
      progessPercent?: number;
      progressStatus?: 'active' | 'exception' | 'success';
    }>) => {
      state.uploadedFileConfig = {
        ...state.uploadedFileConfig,
        ...action.payload,
      };
    },
    updateFileConfig: (state, action: PayloadAction<RequestConfig>) => {
      state.fileConfig = {
        ...state.fileConfig,
        ...action.payload,
      };
    },
    updateFileMapping: (state, action: PayloadAction<any>) => {
      state.fileMapping = {
        ...state.fileMapping,
        ...action.payload,
      };
    },
    setSearchKey: (state, action: PayloadAction<string>) => {
      state.fileMapping.searchKey = action.payload;
    },
    setShowFieldMappingError: (
      state,
      action: PayloadAction<{
        showFieldErrors: boolean;
        total_errors?: number;
        preview?: Array<string[]>;
        link?: string;
      }>,
    ) => {
      state.fileMapping.fieldErrors = {
        ...state.fileMapping.fieldErrors,
        ...action.payload,
      };
    },
  },
});

export const {
  incrementCurrentStep,
  decrementCurrentStep,
  setSearchKey,
  updateUploadedFileConfig,
  updateFileConfig,
  updateFileMapping,
  setShowFieldMappingError,
  resetImportLead,
} = importLeadSlice.actions;

export default importLeadSlice.reducer;
